import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ClientPortalSession {
  token: string;
  brandId: string;
  brandName: string;
  isValid: boolean;
}

interface ClientPortalContextType {
  session: ClientPortalSession | null;
  isLoading: boolean;
  login: (token: string, passcode: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const ClientPortalContext = createContext<ClientPortalContextType | undefined>(undefined);

export function ClientPortalProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<ClientPortalSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session in localStorage
    const savedSession = localStorage.getItem('client-portal-session');
    if (savedSession) {
      try {
        const parsedSession = JSON.parse(savedSession);
        setSession(parsedSession);
      } catch (error) {
        console.error('Invalid saved session:', error);
        localStorage.removeItem('client-portal-session');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (token: string, passcode: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('validate_client_portal_access', {
        p_token: token,
        p_passcode: passcode
      });

      if (error) {
        throw error;
      }

      const result = data as { valid: boolean; brand_id?: string; brand_name?: string };
      
      if (result?.valid) {
        const newSession: ClientPortalSession = {
          token,
          brandId: result.brand_id!,
          brandName: result.brand_name!,
          isValid: true
        };
        
        setSession(newSession);
        localStorage.setItem('client-portal-session', JSON.stringify(newSession));
        
        return { success: true };
      } else {
        return { success: false, error: 'Invalid token or passcode' };
      }
    } catch (error) {
      console.error('Client portal login error:', error);
      return { success: false, error: 'Authentication failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setSession(null);
    localStorage.removeItem('client-portal-session');
  };

  return (
    <ClientPortalContext.Provider value={{ session, isLoading, login, logout }}>
      {children}
    </ClientPortalContext.Provider>
  );
}

export function useClientPortal() {
  const context = useContext(ClientPortalContext);
  if (context === undefined) {
    throw new Error('useClientPortal must be used within a ClientPortalProvider');
  }
  return context;
}